/**
 * Metadata Stats Card Component
 *
 * Displays summary statistics about the filtered demo data:
 * - Date Range: Earliest to latest date in dataset
 * - Location Count: Number of unique locations
 * - Day Count: Number of unique dates
 * - Record Count: Total records (shift-level) or total days (aggregate)
 *
 * Uses useMemo for optimized calculations
 */

import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import {
  DateRange as DateRangeIcon,
  Store as StoreIcon,
  CalendarToday as CalendarIcon,
  DataUsage as DataIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

/**
 * MetadataStatsCard Component
 * @param {Object} props - Component props
 * @param {Array} props.data - Aggregated data array (daily aggregates)
 */
const MetadataStatsCard = ({ data }) => {
  /**
   * Calculate metadata statistics
   * Memoized to avoid recalculation on every render
   */
  const stats = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        dateRange: 'No data',
        locationCount: 0,
        dayCount: 0,
        recordCount: 0
      };
    }

    // Extract unique dates
    const uniqueDates = new Set(data.map(record => record.date));
    const sortedDates = Array.from(uniqueDates).sort();

    // Calculate date range
    let dateRange = 'No data';
    if (sortedDates.length > 0) {
      const startDate = format(parseISO(sortedDates[0]), 'MMM d, yyyy');
      const endDate = format(parseISO(sortedDates[sortedDates.length - 1]), 'MMM d, yyyy');

      if (sortedDates.length === 1) {
        dateRange = startDate;
      } else {
        dateRange = `${startDate} - ${endDate}`;
      }
    }

    // Extract unique location count from aggregated data
    // Use the maximum locationCount as it represents unique locations per day
    const locationCount = Math.max(...data.map(r => r.locationCount || 0), 0);

    // Day count is always the number of unique dates
    const dayCount = uniqueDates.size;

    // Record count depends on view mode
    const recordCount = data.length;

    return {
      dateRange,
      locationCount,
      dayCount,
      recordCount
    };
  }, [data]);

  /**
   * Stat Item Component
   */
  const StatItem = ({ icon: Icon, label, value, color }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2,
        borderRadius: 1,
        backgroundColor: 'rgba(124, 58, 237, 0.04)',
        border: '1px solid rgba(124, 58, 237, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(124, 58, 237, 0.08)'
        }
      }}
    >
      <Icon sx={{ fontSize: 28, color: color || '#7C3AED', mb: 1 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#7C3AED', mb: 0.5, fontSize: '1rem' }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '0.875rem' }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED', mb: 2 }}>
          Data Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatItem
              icon={DateRangeIcon}
              label="Date Range"
              value={stats.dateRange}
              color="#7C3AED"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatItem
              icon={StoreIcon}
              label="Locations"
              value={stats.locationCount}
              color="#7C3AED"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatItem
              icon={CalendarIcon}
              label="Days"
              value={stats.dayCount}
              color="#7C3AED"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatItem
              icon={DataIcon}
              label="Daily Aggregates"
              value={stats.recordCount}
              color="#7C3AED"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MetadataStatsCard;
